import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import { useAppDispatch } from '../hooks/redux';
import { setAuth } from '../store/authSlice';
import { setProfile, setProfileLoading, setProfileError } from '../store/profileSlice';
import { decodeIdToken, isTokenExpired } from '../utils/jwtUtils';
import { apolloClient } from '../apollo';
import { FetchMyProfile } from '../queries/profile';
import { Profile } from '../models/profile';

const AuthHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError('No authentication token provided in URL');
      return;
    }

    const authenticate = async () => {
      try {
        // Decode the ID token to extract user claims
        const claims = decodeIdToken(token);
        console.log('Decoded JWT claims:', claims);
        console.log('Hasura claims:', claims['https://hasura.io/jwt/claims']);
        
        // Validate token expiry
        if (isTokenExpired(token)) {
          setError('Token has expired. Please obtain a new authentication token.');
          return;
        }

        // Extract user info from JWT claims
        const userId = claims.uid || claims.sub;
        const email = claims.email || '';
        const transactorIdStr = claims['https://hasura.io/jwt/claims']?.['x-hasura-transactor-id'];
        const transactorId = transactorIdStr ? Number(transactorIdStr) : null;

        console.log('Extracted values:', { userId, email, transactorId });

        if (!userId) {
          setError('Token does not contain required user ID');
          return;
        }

        if (!transactorId || isNaN(transactorId)) {
          setError('Token does not contain required transactor ID');
          return;
        }

        console.log('transactorid', transactorId)

        // Store token and user data in Redux (and localStorage via authSlice)
        dispatch(
          setAuth({
            token,
            userId,
            email,
            transactorId,
          })
        );

        // Fetch profile after successful authentication
        dispatch(setProfileLoading(true));
        try {
          console.log('fetching profile')
          const { data } = await apolloClient.query({
            query: FetchMyProfile,
          });
          console.log('landing profile', data)
          dispatch(setProfile(Profile.build((data as any).result.profile)));
        } catch (profileError: any) {
          console.error('Error fetching profile:', profileError);
          dispatch(setProfileError(profileError.message || 'Failed to load profile'));
        }

        // Navigate to transactions page
        navigate('/transactions', { replace: true });
      } catch (err: any) {
        console.error('Authentication error:', err);
        setError(err.message || 'Failed to authenticate with provided token');
      }
    };

    authenticate();
  }, [searchParams, navigate, dispatch]);

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <Alert severity="error">{error}</Alert>
        <Typography variant="body2" color="text.secondary">
          Please ensure you have a valid authentication token in the URL
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
      <CircularProgress />
      <Typography variant="body1">Authenticating...</Typography>
    </Box>
  );
};

export default AuthHandler;
